import React from 'react';
import { observer } from 'mobx-react';
import { MeetingTemplatesFormVM } from './meeting-template-form.viewmodel';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormattedMessage, useIntl } from 'react-intl';
import { FieldKey, FORM_CONFIG } from './meeting-template-form.config';
import { FormField } from '../../../global/form/form-field/form-field';
import { Access } from '../../../../api/meetings.dao';
import Text from '../../../global/text/text';
import { RichTextEditor } from '../../../global/rich-text-editor';
import * as mobx from 'mobx';
import './meeting-template.form.scss';
import { MeetingTemplateLinks } from './meeting-template-links/meeting-template-links.view';
import { EnablementStatus } from '../../../../constants/common.constants';
import { cancelText, MORE_BUTTON_TEXT, submitText } from '../../../../constants/common-labels.constants';
import { meetingAccessOptions, meetingStatusOptions } from '../../../../constants/blueprint-labels.constants';
import OptionsButton from '../../../global/options-button/options-button';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import {
    APPLY_CONNECTIONS_TEMPLATE_TEXT,
    ASSIGNED_AS_LANDING_PAGE_TEXT,
    DELETE_MEETING_TEMPLATE_TEXT,
    DISABLE_OTHER_PAGES_TEXT,
    ENABLE_ON_REPLAYS_PAGE_TEXT,
    EXPIRY_DATE_TEMPLATE_INFO_TEXT,
    FEATURED_MEETING_INFO_MESSAGE_TEXT,
    FEATURED_PRESENTATION_TEXT,
    NO_ACCESS_SELECTED_TEXT,
    NO_STATUS_SELECTED_TEXT,
    RECORD_MEETING_ENABLE_TEXT,
    REPLAYS_AVAILABLE_INFO_MESSAGE_TEXT,
    SHOW_LINK_ON_WELCOME_PAGE_TEXT,
    SINGLE_MEETING_CUSTOM_PAGE_TEXT
} from './meeting-template-form.texts';
import { MeetingExpiryDate } from '../../../_events/_manage-event/_meetings-list/_meeting-form/_meeting-expiry-date/meeting-expiry-date';
import { FormFieldInput } from '../../../global/form/form-field-input/form-field-input';
import { NO_TEMPLATE_SELECTED_TEXT } from '../../blueprints.texts';
import { ViewBlueprintDetailsSection } from '../../../_events/_manage-event/shared/view-blueprint-details-section/view-blueprint-details-section';
import { BlueprintType } from '../../../../api/blueprints/blueprint.types';
import { FormFieldProps } from '../../../../infrastructure/form-viewmodel-controller';
import { convertBoolTypeForMixPanel } from '../../../../tracking-service/tracking-values';

export type MeetingTemplateFormPresentationProps = {
    vm: MeetingTemplatesFormVM;
};

const replaysAvailableInfoMessage = <FormattedMessage {...REPLAYS_AVAILABLE_INFO_MESSAGE_TEXT} />;

const featuredMeetingInfoMessage = <FormattedMessage {...FEATURED_MEETING_INFO_MESSAGE_TEXT} />;

export const MeetingTemplateFormPresentation = observer(({ vm }: MeetingTemplateFormPresentationProps): JSX.Element => {
    const intl = useIntl();
    const {
        name,
        description,
        status,
        meetingAccess,
        replayEnabled,
        featured,
        type,
        track,
        topic,
        notes,
        applyConnectionsTemplate,
        connectionsBlueprintId,
        singleMeetingCustomPage,
        label: pageLabel,
        path,
        pageLinkEnabled,
        linkDescription,
        landingPage,
        disableOtherPages,
        recordEnabled,
        externalId
    } = vm.getFormFields(FieldKey);

    const toggle = (field: FormFieldProps): void => field.setValue(!field.value);

    const toggleSingleMeetingCustomPage = (): void => {
        singleMeetingCustomPage.value ? vm.disableSingleMeetingCustomPage() : vm.enableSingleMeetingCustomPage();
    };

    const toggleLandingPage = (): void => {
        landingPage.value ? vm.disableLandingPage() : vm.enableLandingPage();
    };

    const toggleApplyConnectionTemplate = (): void => {
        applyConnectionsTemplate.value ? vm.disableApplyConnectionTemplate() : vm.enableApplyConnectionTemplate();
    };

    const hasDeletePermission = hasPermission(Permission.DELETE_MEETING_TEMPLATES);

    const meetingTemplateOptionsButton = [
        hasDeletePermission && {
            label: intl.formatMessage(DELETE_MEETING_TEMPLATE_TEXT),
            action: vm.deleteMeetingTemplate,
            danger: true
        }
    ].filter(Boolean);

    const showMeetingExpiryDaysInfoMessage = vm.expiryVM?.enabled && !vm.expiryVM.hasValidationError();

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form oe-form--meeting-template'>
                    <div className='oe-form__body'>
                        <FormFieldInput.Text field={name} labelDescriptor={FORM_CONFIG[FieldKey.NAME].labelDescriptor} withError />

                        <FormFieldInput.Text field={description} labelDescriptor={FORM_CONFIG[FieldKey.DESCRIPTION].labelDescriptor} withError />

                        <div className='oe-form__row m-bottom-20px'>
                            <FormFieldInput.Select
                                field={status}
                                labelDescriptor={FORM_CONFIG[FieldKey.MEETING_STATUS].labelDescriptor}
                                options={meetingStatusOptions()}
                                placeholderOptionText={intl.formatMessage(NO_STATUS_SELECTED_TEXT)}
                                fieldClassName='w-40'
                                placeholderOption
                            />

                            <FormFieldInput.Select
                                field={meetingAccess}
                                labelDescriptor={FORM_CONFIG[FieldKey.MEETING_ACCESS].labelDescriptor}
                                options={meetingAccessOptions()}
                                placeholderOptionText={intl.formatMessage(NO_ACCESS_SELECTED_TEXT)}
                                onChangeAction={vm.selectMeetingAccess.bind(vm)}
                                fieldClassName='w-40'
                                placeholderOption
                            />
                        </div>

                        <FormFieldInput.Toggle
                            isSelected={recordEnabled.value as boolean}
                            toggle={() => recordEnabled.setValue(!recordEnabled.value)}
                            text={<Text
                                {...RECORD_MEETING_ENABLE_TEXT}
                                values={{
                                    suffix: convertBoolTypeForMixPanel(!!recordEnabled.value)
                                }}
                                className='replays-toggle-description'
                            />}
                            className='meeting-toggle'
                        />

                        <FormFieldInput.Toggle
                            isSelected={replayEnabled.value as boolean}
                            toggle={() => toggle(replayEnabled)}
                            isDisabled={!meetingAccess.value || meetingAccess.value === Access.Closed || meetingAccess.value === Access.Bookable}
                            text={<Text
                                {...ENABLE_ON_REPLAYS_PAGE_TEXT}
                                values={{
                                    prefix: replayEnabled.value ? 'Meeting is' : 'Not'
                                }}
                                className='replays-toggle-description'
                            />}
                            popoverText={replaysAvailableInfoMessage}
                            className='meeting-toggle'
                        />

                        <FormFieldInput.Toggle
                            isSelected={featured.value as boolean}
                            toggle={() => toggle(featured)}
                            text={<Text
                                {...FEATURED_PRESENTATION_TEXT}
                                values={{
                                    suffix: featured.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                }}
                            />}

                            popoverText={featuredMeetingInfoMessage}
                            className='meeting-toggle'
                        />

                        <MeetingExpiryDate vm={vm.expiryVM} />
                        {showMeetingExpiryDaysInfoMessage && (
                            <div className='meeting-expiry-date-info oe-form-field__info m-bottom-25px'>
                                <FormattedMessage {...EXPIRY_DATE_TEMPLATE_INFO_TEXT} values={{ days: vm.expiryVM.daysValue }} />
                            </div>
                        )}

                        <FormFieldInput.Text field={type} labelDescriptor={FORM_CONFIG[FieldKey.TYPE].labelDescriptor} withError />

                        <FormFieldInput.Text field={track} labelDescriptor={FORM_CONFIG[FieldKey.TRACK].labelDescriptor} withError />

                        <FormFieldInput.Text field={topic} labelDescriptor={FORM_CONFIG[FieldKey.TOPIC].labelDescriptor} withError />

                        <MeetingTemplateLinks vm={vm.linksVM} />

                        <FormField
                            className='m-bottom-30px'
                            label={<FormattedMessage {...FORM_CONFIG[FieldKey.NOTES].labelDescriptor} />}
                        >
                            <RichTextEditor
                                className='m-top-0px'
                                value={notes.value as string}
                                onChange={mobx.action((event, editor) => {
                                    notes.setValue(editor.getData());
                                })}
                                onBlur={notes.setTouched}
                                validationError={
                                    !notes.valid && notes.touched &&
                                    <FormattedMessage
                                        values={{ fieldName: intl.formatMessage(FORM_CONFIG[FieldKey.NOTES].labelDescriptor) }}
                                        {...notes.validationMessage}
                                    />
                                }
                            />
                        </FormField>

                        <FormFieldInput.Toggle
                            isSelected={applyConnectionsTemplate.value as boolean}
                            toggle={toggleApplyConnectionTemplate}
                            text={<Text
                                {...APPLY_CONNECTIONS_TEMPLATE_TEXT}
                                values={{
                                    suffix: applyConnectionsTemplate.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                }}
                            />}
                            className='meeting-toggle'
                        />

                        {
                            applyConnectionsTemplate.value &&
                            <>
                                <FormFieldInput.Select
                                    field={connectionsBlueprintId}
                                    labelDescriptor={FORM_CONFIG[FieldKey.CONNECTIONS_TEMPLATE].labelDescriptor}
                                    customOptions={
                                        vm.connectionsTemplates.map(template => {
                                            return <option value={template.blueprintId} key={template.blueprintId}>{template.name}</option>;
                                        })
                                    }
                                    placeholderOptionText={intl.formatMessage(NO_TEMPLATE_SELECTED_TEXT)}
                                    fieldClassName='m-bottom-10px'
                                    withError
                                    placeholderOption
                                />
                                <ViewBlueprintDetailsSection
                                    blueprintDetails={vm.getSelectedBlueprintDetails()}
                                    blueprintItemType={BlueprintType.CONNECTION}
                                />
                            </>
                        }

                        <FormFieldInput.Toggle
                            isSelected={singleMeetingCustomPage.value as boolean}
                            toggle={toggleSingleMeetingCustomPage}
                            isDisabled={!vm.singleMeetingCustomPageAvailable()}
                            text={<Text
                                {...SINGLE_MEETING_CUSTOM_PAGE_TEXT}
                                values={{
                                    suffix: singleMeetingCustomPage.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                }}
                            />}
                            className='meeting-toggle'
                        />
                        {
                            singleMeetingCustomPage.value &&
                            <>
                                <FormFieldInput.Text field={pageLabel} labelDescriptor={FORM_CONFIG[FieldKey.PAGE_LABEL].labelDescriptor} withError />

                                <FormFieldInput.Text field={path} labelDescriptor={FORM_CONFIG[FieldKey.PAGE_PATH].labelDescriptor} withError />

                                <FormFieldInput.Toggle
                                    isSelected={pageLinkEnabled.value as boolean}
                                    toggle={() => toggle(pageLinkEnabled)}
                                    text={<Text {...SHOW_LINK_ON_WELCOME_PAGE_TEXT} />}
                                    className='meeting-toggle'
                                />

                                <FormFieldInput.Text
                                    field={linkDescription}
                                    labelDescriptor={FORM_CONFIG[FieldKey.LINK_DESCRIPTION].labelDescriptor}
                                    fieldMessageValues={{ prefix: pageLabel.value ? `${pageLabel.value} ` : '' }}
                                    withError
                                />

                                <FormFieldInput.Toggle
                                    isSelected={landingPage.value as boolean}
                                    toggle={toggleLandingPage}
                                    text={<Text
                                        {...ASSIGNED_AS_LANDING_PAGE_TEXT}
                                        values={{ prefix: landingPage.value ? '' : 'Not ' }}
                                    />}
                                    className='meeting-toggle'
                                />

                                <FormFieldInput.Toggle
                                    isSelected={disableOtherPages.value as boolean}
                                    toggle={() => toggle(disableOtherPages)}
                                    isDisabled={!landingPage.value}
                                    text={<Text {...DISABLE_OTHER_PAGES_TEXT} />}
                                    className='meeting-toggle'
                                />
                            </>
                        }

                        <FormFieldInput.Text
                            field={externalId}
                            labelDescriptor={FORM_CONFIG[FieldKey.EXTERNAL_ID].labelDescriptor}
                            withError
                        />
                    </div>
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={submitText(vm.isEditMode)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={vm.submitForm}
                        disabled={vm.isLoading}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                    {vm.isEditMode && !!meetingTemplateOptionsButton.length &&
                        <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT, {
                                suffix: '...'
                            })}
                            options={meetingTemplateOptionsButton}
                            position='top'
                            align='start'
                        />
                    }
                </div>
            }
        />
    );
});
